import React, { useState, useEffect } from 'react';
import { useToast } from '../hooks/useToast';
import { ToastContainer } from './ToastContainer';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Convert local time string "HH:MM" to UTC "HH:MM"
const localToUtcTime = (localTime) => {
  const [h, m] = localTime.split(':').map(Number);
  const now = new Date();
  now.setHours(h, m, 0, 0);
  const utcH = now.getUTCHours();
  const utcM = now.getUTCMinutes();
  return `${String(utcH).padStart(2, '0')}:${String(utcM).padStart(2, '0')}`;
};

// Convert UTC time string "HH:MM" to local "HH:MM"
const utcToLocalTime = (utcTime) => {
  if (!utcTime) return null;
  const parts = utcTime.split(':').map(Number);
  const utcH = parts[0];
  const utcM = parts.length > 1 ? parts[1] : 0;
  const now = new Date();
  now.setUTCHours(utcH, utcM, 0, 0);
  const localH = now.getHours();
  const localM = now.getMinutes();
  return `${String(localH).padStart(2, '0')}:${String(localM).padStart(2, '0')}`;
};

export default function CampaignModal({ type, onClose, onSave }) {
  const { toasts, success, error: toastError } = useToast();
  const isSms = type === 'sms';

  // Helper: get local date N days from today as YYYY-MM-DD
  const getLocalDate = (daysFromToday) => {
    const d = new Date();
    d.setDate(d.getDate() + daysFromToday);
    return d.toISOString().split('T')[0];
  };

  const [formData, setFormData] = useState({
    name: '',
    message_body: '',
    start_date: getLocalDate(1),       // Tomorrow
    end_date: getLocalDate(2),          // 2 days from today
    daily_start_time: '12:00',
    daily_cutoff_time: '20:00',
    retry_count: 0
  });
  const [voiceFile, setVoiceFile] = useState(null);
  const [voiceFilePath, setVoiceFilePath] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testUser, setTestUser] = useState(null);
  const [testUsers, setTestUsers] = useState([]);
  const [testing, setTesting] = useState(false);
  const [durationWarning, setDurationWarning] = useState(false);

  // Fetch users for test dropdown (no search needed - small set)
  useEffect(() => {
    const fetchTestUsers = async () => {
      try {
        const res = await fetch(`${API_URL}/campaigns/test-users?type=${type}`, {
          credentials: 'include'
        });
        if (res.ok) {
          const data = await res.json();
          setTestUsers(data);
        }
      } catch (err) {
        console.error('Error fetching test users:', err);
      }
    };
    fetchTestUsers();
  }, [type]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleVoiceFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check duration using Audio API
    const audio = new Audio();
    audio.src = URL.createObjectURL(file);
    audio.onloadedmetadata = () => {
      if (audio.duration > 60) {
        setDurationWarning(true);
      } else {
        setDurationWarning(false);
      }
    };

    setUploading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('voice_file', file);

      const res = await fetch(`${API_URL}/campaigns/voice/upload`, {
        method: 'POST',
        credentials: 'include',
        body: uploadFormData
      });

      if (!res.ok) throw new Error('Failed to upload voice file');
      const data = await res.json();
      setVoiceFile(file);
      setVoiceFilePath(data.file_path);
      success('Voice file uploaded');
    } catch (err) {
      toastError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleTest = async () => {
    if (!testUser) {
      toastError('Select a user to test');
      return;
    }
    const phone = isSms
      ? testUser.mobile_phone
      : (testUser.mobile_phone || testUser.home_phone);

    // Validate content before testing
    if (isSms && !formData.message_body?.trim()) {
      toastError('Message body is required');
      return;
    }
    if (!isSms && !voiceFilePath) {
      toastError('Voice file is required');
      return;
    }

    setTesting(true);
    try {
      const testRes = await fetch(`${API_URL}/campaigns/test-send`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type,
          message_body: isSms ? formData.message_body : null,
          voice_file_path: !isSms ? voiceFilePath : null,
          phone_number: phone
        })
      });

      if (!testRes.ok) {
        const errData = await testRes.json();
        throw new Error(errData.error || 'Test failed');
      }
      const testData = await testRes.json();
      success(`Test sent to ${testUser.name}! SID: ${testData.sid}`);
    } catch (err) {
      toastError(err.message);
    } finally {
      setTesting(false);
    }
  };

  // Check if test button should be enabled
  const canTest = testUser && (isSms ? formData.message_body?.trim() : voiceFilePath);

  const handleSave = async () => {
    // Validation
    if (!formData.name.trim()) {
      toastError('Campaign name is required');
      return;
    }
    if (isSms && !formData.message_body.trim()) {
      toastError('Message body is required');
      return;
    }
    if (!isSms && !voiceFilePath) {
      toastError('Voice file is required');
      return;
    }
    if (!formData.start_date || !formData.end_date) {
      toastError('Start and end dates are required');
      return;
    }
    if (formData.end_date < formData.start_date) {
      toastError('End date must be >= start date');
      return;
    }

    // Validate time window
    const [startH, startM] = formData.daily_start_time.split(':').map(Number);
    const [cutoffH, cutoffM] = formData.daily_cutoff_time.split(':').map(Number);
    if (startH * 60 + startM >= cutoffH * 60 + cutoffM) {
      toastError('Start time must be before cutoff time');
      return;
    }

    setSaving(true);
    try {
      const campaignBody = {
        name: formData.name,
        type,
        message_body: isSms ? formData.message_body : null,
        voice_file_path: !isSms ? voiceFilePath : null,
        start_date: formData.start_date,
        end_date: formData.end_date,
        daily_start_time: localToUtcTime(formData.daily_start_time),
        daily_cutoff_time: localToUtcTime(formData.daily_cutoff_time),
        retry_count: !isSms ? formData.retry_count : 0
      };

      const res = await fetch(`${API_URL}/campaigns`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(campaignBody)
      });

      if (!res.ok) throw new Error('Failed to create campaign');
      success('Campaign created successfully');
      onSave();
    } catch (err) {
      toastError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <ToastContainer toasts={toasts} />
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            New {isSms ? 'SMS' : 'Voice'} Campaign
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
        </div>

        <div className="p-6 space-y-4">
          {/* Campaign Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="e.g. Summer Sale 2024"
              maxLength={255}
            />
          </div>

          {/* SMS Message Body */}
          {isSms && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
              <textarea
                name="message_body"
                value={formData.message_body}
                onChange={handleChange}
                rows={4}
                maxLength={160}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Enter your SMS message..."
              />
              <div className="text-right mt-1">
                <span className={`text-xs ${
                  formData.message_body.length > 140 ? 'text-yellow-500' : 'text-gray-400'
                } ${formData.message_body.length >= 160 ? 'font-bold text-red-500' : ''}`}>
                  {formData.message_body.length}/160
                </span>
              </div>
            </div>
          )}

          {/* Voice File Upload */}
          {!isSms && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Voice File (MP3) *
              </label>
              <input
                type="file"
                accept=".mp3,audio/mpeg"
                onChange={handleVoiceFileUpload}
                className="w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
              />
              {durationWarning && (
                <p className="mt-1 text-xs text-yellow-600">
                  Voice files over 60 seconds may result in higher costs and lower engagement.
                </p>
              )}
              {uploading && <p className="mt-1 text-xs text-gray-500">Uploading...</p>}
              {voiceFilePath && (
                <p className="mt-1 text-xs text-green-600">File uploaded: {voiceFilePath}</p>
              )}
            </div>
          )}

          {/* Date Range */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
              <input
                type="date"
                name="start_date"
                value={formData.start_date}
                onChange={handleChange}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
              <input
                type="date"
                name="end_date"
                value={formData.end_date}
                onChange={handleChange}
                min={formData.start_date}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          {/* Daily Time Window */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Daily Time Window *</label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Start Time</label>
                <input
                  type="time"
                  name="daily_start_time"
                  value={formData.daily_start_time}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Cutoff Time</label>
                <input
                  type="time"
                  name="daily_cutoff_time"
                  value={formData.daily_cutoff_time}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Campaigns will only send messages/calls during this window each day
            </p>
          </div>

          {/* Retry Count (Voice only) */}
          {!isSms && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Retry Count (0-5)
              </label>
              <input
                type="number"
                name="retry_count"
                value={formData.retry_count}
                onChange={handleChange}
                min={0}
                max={5}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <p className="text-xs text-gray-400 mt-1">
                Failed calls will be retried up to this many times
              </p>
            </div>
          )}

          {/* Try It Out Section */}
          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Try It Out</h3>
            <select
              value={testUser?.id || ''}
              onChange={(e) => {
                const user = testUsers.find(u => u.id === parseInt(e.target.value));
                setTestUser(user || null);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
            >
              <option value="">
                Select a user with {isSms ? 'mobile' : 'mobile or home'} phone...
              </option>
              {testUsers.map(user => {
                const phone = isSms
                  ? user.mobile_phone
                  : (user.mobile_phone || user.home_phone);
                return (
                  <option key={user.id} value={user.id}>
                    {user.name} ({phone || 'No phone'})
                  </option>
                );
              })}
            </select>
            <div className="flex gap-2 mt-2">
              <button
                onClick={handleTest}
                disabled={testing || !canTest}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50"
              >
                {testing ? 'Sending...' : 'Send Test'}
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Sends a test message/call to the selected user
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors font-medium disabled:opacity-50"
          >
            {saving ? 'Creating...' : 'Create Campaign'}
          </button>
        </div>
      </div>
    </div>
  );
}
